/**
 * Hashtags.jsx
 * Which hashtags are worth using.
 *
 * Ordered by how much the answer is actually worth. Your own tags come first —
 * that is the only section about your audience rather than about everyone's,
 * and it needs no API, no quota and no waiting. Instagram's data is second and
 * is rationed, so the remaining allowance is on screen before you spend any of
 * it rather than after. Suggestions are last, because a list of tags nobody
 * has tested is the least useful of the three however long it is.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import { Hash, Warning, Check, Chart, Plus, Eye } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

const VERDICT_TONE = {
  'does well': 'ok',
  'does badly': 'danger',
  'about average': '',
  'not used enough to say': 'muted',
};

export default function Hashtags() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [picked, setPicked] = useState([]);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      setData(await api.hashtags.get(brandId));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
    setPicked([]);
  }, [load]);

  const toggle = (tag) =>
    setPicked((p) => (p.includes(tag) ? p.filter((t) => t !== tag) : [...p, tag]));

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Hashtags</div>
          <h1>Which tags are worth using</h1>
          <p>
            Starting with the ones you have actually used, and how the posts carrying them did —
            the only part of this that is about your audience rather than everyone’s.
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--ok">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {!data ? (
        <div className="card">
          <p className="hint">Loading…</p>
        </div>
      ) : (
        <>
          {picked.length > 0 && (
            <Basket
              picked={picked}
              brandId={brandId}
              onClear={() => setPicked([])}
              onSaved={(name) => {
                setPicked([]);
                setNote(`Saved as “${name}”.`);
                load();
              }}
              onError={setError}
            />
          )}

          <YourTags data={data} picked={picked} onToggle={toggle} />
          <Lookup brandId={brandId} data={data} onDone={load} picked={picked} onToggle={toggle} />
          <Suggest brandId={brandId} picked={picked} onToggle={toggle} onError={setError} />
          <Sets data={data} onUse={(tags) => setPicked(tags)} onDone={load} onError={setError} />
        </>
      )}
    </>
  );
}

/** What has been picked, and what to do with it. */
function Basket({ picked, brandId, onClear, onSaved, onError }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const text = picked.map((t) => `#${t}`).join(' ');

  async function save() {
    setBusy(true);
    try {
      await api.hashtags.saveSet({ brandId, name: name.trim(), tags: picked });
      onSaved(name.trim());
      setName('');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card card--sticky">
      <div className="card__head">
        <div className="card__head-text">
          <h2>
            {picked.length} picked
            {picked.length > 30 && (
              <span className="pill pill--warn">over Instagram’s limit of 30</span>
            )}
          </h2>
          <p className="mono-wrap">{text}</p>
        </div>
      </div>

      <div className="inline-form" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
        <button className="btn" onClick={() => navigator.clipboard?.writeText(text)}>
          Copy
        </button>

        <div className="field">
          <label htmlFor="ht-name">Save as a set</label>
          <input
            id="ht-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Product launch"
          />
        </div>

        <button className="btn btn--primary" onClick={save} disabled={busy || !name.trim()}>
          <Check /> Save
        </button>
        <button className="btn" onClick={onClear}>
          Clear
        </button>
      </div>
    </section>
  );
}

/** The section no competitor can produce for you. */
function YourTags({ data, picked, onToggle }) {
  const { own } = data;

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Chart size={21} />
        </span>
        <div className="card__head-text">
          <h2>Your own tags</h2>
          <p>
            {own.sample === 0
              ? 'No published posts with hashtags yet.'
              : `From ${own.sample} published post${own.sample === 1 ? '' : 's'}, measured as ` +
                `${own.measure === 'rate' ? 'engagement per person reached' : 'total engagement'}. ` +
                `A tag needs ${own.minPostsForVerdict} posts before this will call it either way.`}
          </p>
        </div>
      </div>

      {own.tags.length === 0 ? (
        <p className="hint">
          Publish a few posts with hashtags and this fills in on its own — nothing to set up.
        </p>
      ) : (
        <div className="tagtable">
          {own.tags.slice(0, 40).map((t) => (
            <button
              key={t.tag}
              type="button"
              className={`tagrow${picked.includes(t.tag) ? ' is-picked' : ''}`}
              onClick={() => onToggle(t.tag)}
            >
              <span className="tagrow__tag">{t.display}</span>
              <span className={`badge${VERDICT_TONE[t.verdict] ? ` badge--${VERDICT_TONE[t.verdict]}` : ''}`}>
                {t.verdict}
              </span>
              <span className="tagrow__meta">
                {t.posts} post{t.posts === 1 ? '' : 's'}
                {t.confident && t.lift !== 0 && (
                  <span className={t.lift > 0 ? 'delta delta--up' : 'delta delta--down'}>
                    {t.lift > 0 ? '+' : ''}
                    {t.lift}%
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/** Instagram's own data, and the allowance it costs. */
function Lookup({ brandId, data, onDone, picked, onToggle }) {
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const quota = data.quota;
  const clean = tag.trim().replace(/^#/, '').toLowerCase();
  const free = quota.free.includes(clean);
  const blocked = !free && quota.remaining <= 0;

  async function look() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.hashtags.lookup({ brandId, tag: clean }));
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Eye size={21} />
        </span>
        <div className="card__head-text">
          <h2>Look one up on Instagram</h2>
          <p>
            How busy a tag is, from the posts currently at the top of it.
            {' '}
            <b>
              {quota.remaining} of {quota.limit} lookups left
            </b>{' '}
            this week
            {quota.resetsAt ? `, next one back ${formatLondon(quota.resetsAt)}` : ''}.
          </p>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      <div className="inline-form" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
        <div className="field">
          <label htmlFor="ht-look">Hashtag</label>
          <input
            id="ht-look"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder="#productivity"
            onKeyDown={(e) => e.key === 'Enter' && clean && !blocked && look()}
          />
          <p className="hint">
            {!clean
              ? 'Instagram allows thirty different tags a week per account. Re-checking one you have already looked up is free.'
              : free
                ? 'Already looked up this week — checking again costs nothing.'
                : blocked
                  ? 'No lookups left this week. Tags you have already checked can still be re-checked.'
                  : `This would use one of your ${quota.remaining} remaining.`}
          </p>
        </div>
        <button className="btn btn--primary" onClick={look} disabled={busy || !clean || blocked}>
          {busy ? 'Asking Instagram…' : 'Look up'}
        </button>
      </div>

      {result && (
        <div className="lookup">
          <div className="lookup__head">
            <h3>{result.display}</h3>
            <span className="badge badge--accent">{result.competitiveness}</span>
            {result.cached && <span className="badge">cached {formatLondon(result.lookedUpAt)}</span>}
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => onToggle(result.tag)}
            >
              {picked.includes(result.tag) ? 'Picked' : <><Plus size={14} /> Pick</>}
            </button>
          </div>

          <p className="hint">{result.note}</p>

          {result.samples > 0 && (
            <div className="stat-row">
              <div className="stat">
                <div className="stat__value">{result.medianEngagement.toLocaleString()}</div>
                <div className="stat__label">Median on the top posts</div>
                <div className="stat__hint">What it takes to compete here</div>
              </div>
              <div className="stat">
                <div className="stat__value">{result.topEngagement.toLocaleString()}</div>
                <div className="stat__label">The very top post</div>
              </div>
            </div>
          )}

          {result.examples?.length > 0 && (
            <details className="details">
              <summary>The posts at the top of this tag</summary>
              <ul className="plainlist">
                {result.examples.map((e, i) => (
                  <li key={i}>
                    <b>
                      {e.likes.toLocaleString()} likes, {e.comments.toLocaleString()} comments
                    </b>
                    {e.caption ? ` — ${e.caption}` : ''}{' '}
                    {e.permalink && (
                      <a href={e.permalink} target="_blank" rel="noreferrer">
                        open
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {data.lookups.length > 0 && (
        <details className="details">
          <summary>{data.lookups.length} looked up this week</summary>
          <div className="chips" style={{ marginTop: 10 }}>
            {data.lookups.map((l) => (
              <button
                key={l.tag}
                type="button"
                className={`chip-btn${picked.includes(l.tag) ? ' is-on' : ''}`}
                onClick={() => onToggle(l.tag)}
                title={`${l.competitiveness ?? 'unknown'} · median ${l.median_engagement ?? '?'}`}
              >
                {l.display}
              </button>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

/** Candidates from the model. */
function Suggest({ brandId, picked, onToggle, onError }) {
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const KINDS = {
    broad: 'Reach, little chance of surfacing',
    niche: 'Where a smaller account is found',
    community: 'Followed, not just searched',
    brand: 'Yours alone',
  };

  async function go() {
    setBusy(true);
    try {
      setResult(await api.hashtags.suggest({ brandId, topic: topic.trim() }));
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  const grouped = new Map();
  for (const s of result?.suggestions ?? []) {
    if (!grouped.has(s.kind)) grouped.set(s.kind, []);
    grouped.get(s.kind).push(s);
  }

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Hash size={21} />
        </span>
        <div className="card__head-text">
          <h2>Suggest some</h2>
          <p>In this brand’s voice, avoiding its banned claims, and told what already works.</p>
        </div>
      </div>

      <div className="inline-form" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
        <div className="field">
          <label htmlFor="ht-topic">What is the post about</label>
          <input
            id="ht-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="a new keyboard layout for one-handed typing"
          />
        </div>
        <button className="btn btn--primary" onClick={go} disabled={busy || !topic.trim()}>
          {busy ? 'Thinking…' : 'Suggest'}
        </button>
      </div>

      {result && (
        <>
          {result.basedOn.working.length > 0 && (
            <p className="hint">
              Told that {result.basedOn.working.map((t) => `#${t}`).join(', ')} already work for you,
              so these sit alongside them rather than repeating them.
            </p>
          )}

          {[...grouped.entries()].map(([kind, items]) => (
            <div key={kind} className="evg-group">
              <h3 className="evg-group__head">
                {kind}
                <span className="muted">{KINDS[kind]}</span>
              </h3>
              <div className="chips">
                {items.map((s) => (
                  <button
                    key={s.tag}
                    type="button"
                    className={`chip-btn${picked.includes(s.tag) ? ' is-on' : ''}`}
                    onClick={() => onToggle(s.tag)}
                    title={s.why}
                  >
                    {s.display}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <p className="hint" style={{ marginTop: 12 }}>
            Cost {(result.costPence / 100).toFixed(3)}p · {result.model}
          </p>
        </>
      )}
    </section>
  );
}

/** Saved groups. */
function Sets({ data, onUse, onDone, onError }) {
  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>Saved sets</h2>
          <p>Groups you drop into a post in one go.</p>
        </div>
      </div>

      {data.sets.length === 0 ? (
        <p className="hint">None yet. Pick some tags above and save them as a set.</p>
      ) : (
        <div className="rows">
          {data.sets.map((set) => (
            <div className="row" key={set.id}>
              <div style={{ minWidth: 0 }}>
                <div className="row__label">{set.name}</div>
                <div className="row__sub mono-wrap">{set.tags.map((t) => `#${t}`).join(' ')}</div>
              </div>
              <div className="row__end">
                <span className="badge">{set.tags.length}</span>
                <button className="btn btn--sm" onClick={() => onUse(set.tags)}>
                  Pick these
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  onClick={async () => {
                    try {
                      await api.hashtags.removeSet(set.id);
                      onDone();
                    } catch (e) {
                      onError(e);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
